import { createSlice, PayloadAction } from "@reduxjs/toolkit";

// Session expires after 1 hour (in milliseconds)
const SESSION_EXPIRY_MS = 60 * 60 * 1000;

interface ActingOnBehalfState {
  isActive: boolean;
  targetMemberId: string | null;
  targetMemberName: string | null;
  familyId: string | null;
  startedAt: string | null;
  expiresAt: string | null;
}

const initialState: ActingOnBehalfState = {
  isActive: false,
  targetMemberId: null,
  targetMemberName: null,
  familyId: null,
  startedAt: null,
  expiresAt: null,
};

/**
 * Check if a session has expired based on expiresAt timestamp
 */
export const isSessionExpired = (expiresAt: string | null): boolean => {
  if (!expiresAt) return true;
  return new Date(expiresAt).getTime() < Date.now();
};

export const actingOnBehalfSlice = createSlice({
  name: "actingOnBehalf",
  initialState,
  reducers: {
    startActingOnBehalf: (
      state,
      action: PayloadAction<{
        memberId: string;
        memberName: string;
        familyId: string;
      }>
    ) => {
      const now = new Date();
      const expiresAt = new Date(now.getTime() + SESSION_EXPIRY_MS);

      state.isActive = true;
      state.targetMemberId = action.payload.memberId;
      state.targetMemberName = action.payload.memberName;
      state.familyId = action.payload.familyId;
      state.startedAt = now.toISOString();
      state.expiresAt = expiresAt.toISOString();
    },
    stopActingOnBehalf: (state) => {
      state.isActive = false;
      state.targetMemberId = null;
      state.targetMemberName = null;
      state.familyId = null;
      state.startedAt = null;
      state.expiresAt = null;
    },
    // Called on app init to check and clear expired sessions
    validateSession: (state) => {
      if (state.isActive && isSessionExpired(state.expiresAt)) {
        state.isActive = false;
        state.targetMemberId = null;
        state.targetMemberName = null;
        state.familyId = null;
        state.startedAt = null;
        state.expiresAt = null;
      }
    },
    // Extend session (e.g., on user activity)
    extendSession: (state) => {
      if (state.isActive && !isSessionExpired(state.expiresAt)) {
        state.expiresAt = new Date(Date.now() + SESSION_EXPIRY_MS).toISOString();
      }
    },
  },
});

export const {
  startActingOnBehalf,
  stopActingOnBehalf,
  validateSession,
  extendSession,
} = actingOnBehalfSlice.actions;
export default actingOnBehalfSlice.reducer;
